import cn from 'classnames';
import AnimatedImage from 'core-lib/components/AnimatedImage';
import Swipeable from 'core-lib/components/Swipeable';
import formatCurrency from 'core-lib/utils/currency';
import { customToast } from 'core-lib/utils/customToast';
import { formatFuelBalance } from 'core-lib/utils/fuel';
import vibrate from 'core-lib/utils/vibration';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import ShipRocketDeliveryDate from 'src/modules/parkplusStoreV2/components/Cart/ShipRocketDeliveryDate';
import ShipRocketShimmer from 'src/modules/parkplusStoreV2/components/Cart/ShipRocketDeliveryDate/ShipRocketShimmer';
import parkplusStoreV2RedirectionRoutes from 'src/modules/parkplusStoreV2/constants/parkplusStoreV2RedirectionRoutes';
import useCart from 'src/modules/parkplusStoreV2/hooks/useCart';
import useParkplusStoreV2 from 'src/modules/parkplusStoreV2/hooks/useParkplusStoreV2';
import {
    CartItem as CartItemType,
    ShippingDataType,
} from 'src/modules/parkplusStoreV2/types/cart';
import { trackCtaClickedStoreV2 } from 'src/modules/parkplusStoreV2/utils/events';
import { Button } from 'ui-v2/components/Button';
import { Divider } from 'ui-v2/components/Divider';
import Icon from 'ui-v2/components/Icon/Icon';
import Stepper from 'ui-v2/components/Stepper';
import { Typography } from 'ui-v2/components/Typography';
import s from './CartItem.module.scss';
import CartItemPopUp from './CartItemPopUp';

type cartItemPropType = {
  cartItem: CartItemType;
  onClose?: () => void;
  index?: number;
  shppingData?: ShippingDataType[];
  error?: boolean;
  loading?: boolean;
  showDeliveryDate?: boolean;
};
export const CartItem = ({
  cartItem,
  onClose,
  index,
  shppingData,
  error = false,
  loading = false,
  showDeliveryDate = false,
}: cartItemPropType) => {
  //state
  const [flip, setFlip] = useState(false);
  const [height, setHeight] = useState(0);
  const [swipeDist, setSwipeDist] = useState(0);
  const [initialPos, setInitialPos] = useState(0);

  // declarations
  const router = useRouter();
  // custom hooks
  const { handleUpdateCart } = useCart();
  const { cartData } = useParkplusStoreV2();
  const { cartId } = cartData || {};

  const ItemRef = useRef<HTMLDivElement>(null);
  // constants
  const {
    productId,
    productType,
    coverImage,
    title,
    subtitle1,
    price,
    discountedPrice,
    quantity,
    mlUsed,
    maxCartQuantity,
    stock,
    attributes,
  } = cartItem;
  const isOutOfStock = stock === 0;
  const giftItems = cartData?.giftSection?.giftItems;
  const giftIndex =
    giftItems?.length > 0
      ? giftItems?.findIndex((item) => +item.productId == +productId)
      : -1;

  const isGift = useMemo(() => {
    if (giftIndex !== -1) return true;
    return false;
  }, [giftIndex]);

  useEffect(() => {
    const trashTransition = localStorage.getItem('trashTransition');
    setHeight(ItemRef?.current?.clientHeight);
    if (!trashTransition && index == 0) {
      setTimeout(() => {
        setSwipeDist(35);
        setInitialPos(35);
      }, 1000);
      setTimeout(() => {
        localStorage.setItem('trashTransition', 'true');
        setSwipeDist(0);
        setInitialPos(0);
      }, 2700);
    }
  }, []);

  const cartSize = cartData?.data?.length;

  const updateQuantity = (newQuantity: number, currentQuantity: number) => {
    trackCtaClickedStoreV2(
      `${productId}`,
      newQuantity > currentQuantity ? 'plus' : 'minus',
      { cart_id: cartId }
    );

    if (newQuantity > maxCartQuantity && newQuantity > currentQuantity) {
      customToast({
        label: `You can only add up to ${maxCartQuantity} of these items`,
        variant: 'criticalNotPriority',
      });
      return;
    }

    if (newQuantity === 0 && !flip && !isOutOfStock) {
      setFlip(true);
    } else {
      handleUpdateCart({ ...cartItem, quantity: newQuantity });
      setFlip(false);
    }
  };

  const deliveryDateSection = useMemo(() => {
    if (!showDeliveryDate) {
      if (cartItem?.productType !== 'virtual') {
        if (
          shppingData?.length === 0 ||
          shppingData?.length !== cartSize ||
          loading
        ) {
          return <ShipRocketShimmer error={error} />;
        }
        return shppingData?.map((item, id) => {
          if (item?.itemId === productId) {
            return (
              <ShipRocketDeliveryDate
                item={item}
                key={`date-${id}`}
                error={error}
              />
            );
          }
          return null;
        });
      }
    }
    return null;
  }, [
    showDeliveryDate,
    cartItem?.productType,
    shppingData,
    cartSize,
    loading,
    error,
    productId,
  ]);

  switch (flip) {
    case true:
      return (
        <div
          className={s.flip}
          style={
            height && {
              height: height,
            }
          }
        >
          <div className={s.flippedCard}>
            {flip == true && (
              <CartItemPopUp
                title={title}
                onClose={() => {
                  setFlip(false);
                  trackCtaClickedStoreV2('', 'no');
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setFlip(false);
                  updateQuantity(0, 1);
                  trackCtaClickedStoreV2('', 'yes');
                }}
              />
            )}
          </div>
        </div>
      );
    default:
      return (
        <div
          style={{
            position: 'relative',
            overflow: 'hidden',
          }}
          ref={ItemRef}
        >
          <div
            style={{
              background: `red`,
              height: `100%`,
              width: `${swipeDist}px`,
              position: `absolute`,
              left: `0`,
              top: `0`,
              display: 'block',
              transition: initialPos ? `width 0.3s ease 0s` : 'none',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: `center`,
                justifyContent: 'center',
                height: `100%`,
              }}
            >
              <Icon name="TrashRegular" color="on_dark_bg" width={24} height={24} />
            </div>
          </div>
          <Swipeable
            onSwiping={({ difference }) => {
              setSwipeDist(() => Math.max(difference, 0));
            }}
            onSwipeEnd={() => {
              setSwipeDist(0);
            }}
            exitThreshold={0.3}
            onExit={() => {
              updateQuantity(0, 1);
              vibrate();
            }}
            intialPos={initialPos}
            allowedSwipeDirection="right"
            rightToLeftSwipe
          >
            <div className={cn(s.item)}>
              <div className={s.upperContent}>
                <div
                  onClick={() => {
                    router.push(
                      `${parkplusStoreV2RedirectionRoutes.pdp}${productId}`
                    );

                    if (onClose) onClose();
                  }}
                  className={cn(s.imageWrapper)}
                >
                  <AnimatedImage
                    src={coverImage}
                    className={cn(s.image, {
                      [s.fade]:
                        isOutOfStock ||
                        (isGift &&
                          !giftItems?.[giftIndex]?.giftDetails?.isApplicable),
                    })}
                    customStyle={{
                      objectFit: 'cover',
                      borderRadius: 8,
                    }}
                    alt="productImage"
                  />
                </div>
                <div className={s.info}>
                  <div className={s.textStack}>
                    <div className={s.top}>
                      <div className={s.text}>
                        <Typography
                          label={title}
                          variant="label_3_medium"
                          className={s.lineclamp}
                          color="secondary"
                          as="h3"
                        />
                        <Typography
                          label={subtitle1}
                          variant="label_4_regular"
                          color="tertiary"
                        />
                        {attributes?.map((item, index) => (
                          <div key={index}>
                            <Typography
                              label={`${item.title}: `}
                              color="secondary"
                              variant="label_3_medium"
                            />
                            <Typography
                              label={item.value}
                              color="secondary"
                              variant="label_3_medium"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className={s.between}>
                      <Typography
                        label={formatCurrency(price)}
                        variant="label_3_regular"
                        style={{
                          marginRight: 6,
                          textDecoration: 'line-through',
                        }}
                        color="secondary"
                      />
                      <Typography
                        label={formatCurrency(discountedPrice)}
                        variant="label_3_bold"
                      />

                      {!isOutOfStock && !isGift && mlUsed > 0 && (
                        <div className={s.mlPrice}>
                          &nbsp;
                          <Typography
                            label={`with `}
                            color="secondary"
                            variant="body_4_medium"
                          />
                          <AnimatedImage
                            src="https://parkplus-bkt-img.parkplus.io/production/team/public/FS_20240104160730062768.png"
                            className={s.drop}
                            alt="dropIcon"
                          />
                          <Typography
                            label={formatFuelBalance(mlUsed)}
                            color="secondary"
                            variant="body_4_medium"
                          />
                        </div>
                      )}
                      {isGift && (
                        <AnimatedImage
                          src="https://parkplus-bkt-img.parkplus.io/production/team/public/FS_20240517175307240520.png"
                          className={s.giftIcon}
                          alt="giftIcon"
                        />
                      )}
                    </div>
                  </div>
                  <div>
                    {isOutOfStock ||
                    (isGift &&
                      !giftItems?.[giftIndex]?.giftDetails?.isApplicable) ? (
                      <Button
                        label="Remove"
                        variant="secondary_color"
                        size="small"
                        onClick={() => {
                          updateQuantity(0, 1);
                          trackCtaClickedStoreV2('', 'remove');
                        }}
                      />
                    ) : (
                      <Stepper
                        count={quantity}
                        maxLimit={maxCartQuantity}
                        size="small"
                        onChange={(newQuantity) => {
                          updateQuantity(newQuantity, quantity);
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
              {!isOutOfStock && (
                <div className={s.checkDelivery}>{deliveryDateSection}</div>
              )}
            </div>
            {(isOutOfStock ||
              (isGift &&
                !giftItems?.[giftIndex]?.giftDetails?.isApplicable)) && (
              <div className={s.soldout}>
                <div className={s.text}>
                  <Typography
                    label={
                      isOutOfStock
                        ? 'Item out of stock.'
                        : giftItems?.[giftIndex]?.giftDetails?.title
                    }
                    variant="tiny_medium"
                    color="negative"
                  />
                  &nbsp;
                  {isOutOfStock ? (
                    <Typography
                      label="Remove this item to proceed payment"
                      variant="tiny_medium"
                      color="secondary"
                    />
                  ) : (
                    <Typography
                      label={giftItems?.[giftIndex]?.giftDetails?.subtitle}
                      variant="tiny_medium"
                      color="secondary"
                    />
                  )}
                </div>
              </div>
            )}
          </Swipeable>
          <Divider variant="full" />
        </div>
      );
  }
};
